import { Stack, Table, TableBody, TableContainer, TableHead } from '@cmg/design-system';
import { FormikSelectField } from '@cmg/design-system-formik';
import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';

import { InterestLevelInterestUnit, SecurityType } from '../../../../../../../../graphql';
import { IndicationForm_OfferingPartsFragment } from '../../../indication-form/graphql/__generated__';
import InterestLevelsHeaderRow from '../indication-section-read-view/interest-levels-header-row/InterestLevelsHeaderRow';
import { IndicationFormValues, InterestLevelValue } from '../utils/IndicationSectionValidation';
import InterestLevelFormRow from './interest-level-form-row/InterestLevelFormRow';
import { getInterestUnitFieldOptions } from './interest-level-form-row/InterestLevelFormRow.model';
import { LimitIndicationsOnlyNote } from './LimitIndicationsOnlyNote';
import { ShareIndicationsOnlyNote } from './ShareIndicationsOnlyNote';

export type InterestLevelTableFormProps = Readonly<{
  interestLevels: InterestLevelValue[];
  currencyCode: string;
  onRemove: (index: number) => void;
  isLimitIndicationsOnlyEnabled: boolean;
  isOnlySharesUnitOnIndicationsEnabled: boolean;
  offeringType: IndicationForm_OfferingPartsFragment['type'];
  isDepositoryReceiptEnabled: boolean;
  securityType: SecurityType | null;
}>;

export function InterestLevelTableForm({
  interestLevels,
  onRemove,
  isOnlySharesUnitOnIndicationsEnabled,
  isLimitIndicationsOnlyEnabled,
  offeringType,
  isDepositoryReceiptEnabled,
  securityType,
}: InterestLevelTableFormProps) {
  const { setFieldValue } = useFormikContext<IndicationFormValues>();

  const interestUnitFieldOptions = useMemo(() => {
    return getInterestUnitFieldOptions(
      isOnlySharesUnitOnIndicationsEnabled,
      isDepositoryReceiptEnabled,
      securityType
    );
  }, [isDepositoryReceiptEnabled, isOnlySharesUnitOnIndicationsEnabled, securityType]);

  const handleChangeInterestUnit = useCallback(
    (newInterestUnit: InterestLevelInterestUnit) => {
      setFieldValue('selectedInterestUnit', newInterestUnit);
      setFieldValue(
        'interestLevels',
        interestLevels.map(level => ({ ...level, interestUnit: newInterestUnit }))
      );
    },
    [interestLevels, setFieldValue]
  );

  const demandQuantityAdornmentOverride =
    securityType === SecurityType.CommonOrdinaryShare && isDepositoryReceiptEnabled ? 'DRs' : null;

  return (
    <Stack gap={1}>
      <FormikSelectField
        select
        label="Demand Unit"
        showHelperTextInTooltip
        name="selectedInterestUnit"
        sx={{ width: 187 }}
        onChange={handleChangeInterestUnit}
        options={interestUnitFieldOptions}
      />
      <TableContainer>
        <Table sx={{ width: 650 }}>
          <TableHead>
            <InterestLevelsHeaderRow isEditing />
          </TableHead>
          <TableBody>
            {interestLevels.map((rowValue, index) => (
              <InterestLevelFormRow
                key={index}
                index={index}
                onRemove={() => onRemove(index)}
                rowValue={rowValue}
                offeringType={offeringType}
                isLimitIndicationsOnlyEnabled={isLimitIndicationsOnlyEnabled}
                demandQuantityAdornmentOverride={demandQuantityAdornmentOverride}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isLimitIndicationsOnlyEnabled && <LimitIndicationsOnlyNote />}
      {isOnlySharesUnitOnIndicationsEnabled && <ShareIndicationsOnlyNote />}
    </Stack>
  );
}
